/**
 * MADEKTA — quote-form.js
 * Validación accesible de los formularios de cotización y contacto. En modo
 * WhatsApp arma el mensaje con los datos y abre la conversación con el equipo
 * de cotizaciones; las fotos y planos se envían en el chat.
 */
import { validateField } from '@madekta/validation';
/** @param {HTMLFormElement} form */
function enhanceValidation(form) {
  const fields = () =>
    /** @type {(HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement)[]} */ ([
      ...form.querySelectorAll('input:not([type="hidden"]):not([tabindex="-1"]), select, textarea'),
    ]);

  const show = (field, message) => {
    const slot = form.querySelector(`[data-error-for="${field.id}"]`);
    field.setAttribute('aria-invalid', String(Boolean(message)));
    if (slot) {
      slot.textContent = message ?? '';
      slot.id ||= `${field.id}-error`;
      const described = new Set((field.getAttribute('aria-describedby') || '').split(' ').filter(Boolean));
      if (message) described.add(slot.id);
      else described.delete(slot.id);
      field.setAttribute('aria-describedby', [...described].join(' '));
    }
  };

  form.addEventListener(
    'blur',
    (event) => {
      const field = event.target;
      if (field.matches?.('input, select, textarea') && field.id) show(field, validateField(field));
    },
    true,
  );

  return () => {
    let firstInvalid = null;
    for (const field of fields()) {
      if (!field.id) continue;
      const message = validateField(field);
      show(field, message);
      if (message && !firstInvalid) firstInvalid = field;
    }
    firstInvalid?.focus();
    return !firstInvalid;
  };
}

/* Formulario nativo (sin número de WhatsApp): solo validación. */
document.querySelectorAll('form.quote-form:not(quote-form form)').forEach((form) => {
  const validate = enhanceValidation(form);
  form.setAttribute('novalidate', '');
  form.addEventListener('submit', (event) => {
    if (!validate()) event.preventDefault();
  });
});

/** Texto visible de la etiqueta de un campo, sin «(opcional)». */
function labelOf(form, field) {
  const label = form.querySelector(`label[for="${field.id}"]`);
  return label?.firstChild?.textContent.trim() ?? '';
}

class QuoteForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.status = this.querySelector('[data-form-status]');
    this.validate = enhanceValidation(this.form);
    this.form.setAttribute('novalidate', '');
    this.form.addEventListener('submit', (event) => this.onSubmit(event));

    /* En personalizaciones, el enlace lleva la variante elegida. */
    document.addEventListener('madekta:variant-changed', (event) => {
      if (event.detail?.id) this.variantId = String(event.detail.id);
    });
  }

  message() {
    const lines = ['Hola MADEKTA, quiero cotizar.', ''];
    const { product, productUrl, service } = this.dataset;
    if (product) {
      const url = this.variantId ? `${productUrl}?variant=${this.variantId}` : productUrl;
      lines.push(`Referencia: ${product} (${url})`);
    }
    if (service) lines.push(`Servicio: ${service}`);

    let description = '';
    this.form.querySelectorAll('input:not([type="checkbox"]), select, textarea').forEach((field) => {
      const value = field.value.trim();
      if (!value) return;
      if (field.name === 'text') description = value;
      else lines.push(`${labelOf(this.form, field)}: ${value}`);
    });
    if (description) lines.push('', description);
    lines.push('', 'Te envío fotos o planos por aquí.');
    return lines.join('\n');
  }

  onSubmit(event) {
    event.preventDefault();
    if (!this.validate()) {
      this.setStatus('error', 'Revisa los campos marcados.');
      return;
    }
    const url = `https://wa.me/${this.dataset.number}?text=${encodeURIComponent(this.message())}`;
    window.open(url, '_blank', 'noopener');
    this.setStatus(
      'success',
      'Abrimos WhatsApp con tu mensaje. Envíalo y adjunta allí las fotos, medidas o planos. ',
      url,
    );
  }

  setStatus(type, message, url) {
    this.status.hidden = !message;
    this.status.className = `form-status form-status--${type}`;
    this.status.textContent = message;
    if (url) {
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.textContent = '¿No se abrió? Abrir WhatsApp';
      this.status.append(link);
      this.status.focus();
    }
  }
}
customElements.define('quote-form', QuoteForm);
